import React, { useState, useEffect } from 'react';
import { X, Save, Eye, Brain, Shield, Plus, Trash2 } from 'lucide-react';
import './ThresholdModal.css';

interface PerImageConfig {
  face_detect?: boolean;
  ai_image_detect?: boolean;
}

interface ImageSettingsModalProps {
  globalConfig: {
    face_detect: boolean;
    ai_image_detect: boolean;
  };
  perImageConfig: Record<string, PerImageConfig>;
  imageBlock: boolean;
  onSave: (
    globalConfig: { face_detect: boolean; ai_image_detect: boolean },
    perImageConfig: Record<string, PerImageConfig>,
    imageBlock: boolean
  ) => void;
  onClose: () => void;
}

const ImageSettingsModal: React.FC<ImageSettingsModalProps> = ({
  globalConfig,
  perImageConfig,
  imageBlock,
  onSave,
  onClose
}) => {
  const [globalFaceDetect, setGlobalFaceDetect] = useState(globalConfig.face_detect);
  const [globalAiImageDetect, setGlobalAiImageDetect] = useState(globalConfig.ai_image_detect);
  const [imageBlockEnabled, setImageBlockEnabled] = useState(imageBlock);
  const [perImageSettings, setPerImageSettings] = useState<Record<string, PerImageConfig>>(perImageConfig || {});
  const [newImageIndex, setNewImageIndex] = useState<string>('');

  useEffect(() => {
    setGlobalFaceDetect(globalConfig.face_detect);
    setGlobalAiImageDetect(globalConfig.ai_image_detect);
    setImageBlockEnabled(imageBlock);
    setPerImageSettings(perImageConfig || {});
  }, [globalConfig, perImageConfig, imageBlock]);

  const handleSave = () => {
    // At least one global option must be enabled
    if (!globalFaceDetect && !globalAiImageDetect) {
      alert('At least one detection option must be enabled in global configuration (Face Detection or AI Image Detection)');
      return;
    }

    // Validate per-image configs
    for (const [index, config] of Object.entries(perImageSettings)) {
      const idx = parseInt(index);
      if (isNaN(idx) || idx < 0 || idx > 9) {
        alert(`Invalid image index: ${index}. Image indices must be between 0 and 9.`);
        return;
      }
      // At least one option must be enabled for per-image configs
      if (config.face_detect === false && config.ai_image_detect === false) {
        // This is allowed - it means both are disabled for this image
      }
    }

    onSave(
      {
        face_detect: globalFaceDetect,
        ai_image_detect: globalAiImageDetect
      },
      perImageSettings,
      imageBlockEnabled
    );
  };

  const addPerImageConfig = () => {
    const index = parseInt(newImageIndex);
    if (isNaN(index) || index < 0 || index > 9) {
      alert('Image index must be a number between 0 and 9');
      return;
    }
    if (perImageSettings[newImageIndex]) {
      alert(`Configuration for image index ${newImageIndex} already exists`);
      return;
    }
    setPerImageSettings({
      ...perImageSettings,
      [newImageIndex]: {
        face_detect: globalFaceDetect,
        ai_image_detect: globalAiImageDetect
      }
    });
    setNewImageIndex('');
  };

  const removePerImageConfig = (index: string) => {
    const newSettings = { ...perImageSettings };
    delete newSettings[index];
    setPerImageSettings(newSettings);
  };

  const updatePerImageConfig = (index: string, field: 'face_detect' | 'ai_image_detect', value: boolean) => {
    setPerImageSettings({
      ...perImageSettings,
      [index]: {
        ...perImageSettings[index],
        [field]: value
      }
    });
  };

  const sortedImageIndices = Object.keys(perImageSettings).sort((a, b) => parseInt(a) - parseInt(b));

  return (
    <div className="threshold-modal-overlay" onClick={onClose}>
      <div className="threshold-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
        <div className="threshold-modal-header">
          <h2>Image Settings</h2>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="threshold-modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>

          <div className="threshold-controls">
            {/* Global Configuration */}
            <div className="threshold-section">
              <label className="threshold-label">Global Configuration</label>
              <p className="webhook-help" style={{ marginBottom: '1rem' }}>
                These settings apply to all images by default. Individual images can override these settings using per-image configuration below.
              </p>
              
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '1rem', 
                marginTop: '1rem',
                padding: '1.5rem',
                background: 'var(--landing-bg-soft)',
                border: '1px solid var(--landing-border)'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '0',
                  border: '1px solid var(--landing-border)',
                  background: globalFaceDetect ? 'var(--landing-accent-soft)' : 'transparent',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={globalFaceDetect}
                    onChange={(e) => setGlobalFaceDetect(e.target.checked)}
                    style={{ 
                      width: '18px', 
                      height: '18px', 
                      cursor: 'pointer',
                      accentColor: 'var(--landing-accent)'
                    }}
                  />
                  <Eye size={18} color={globalFaceDetect ? 'var(--landing-accent)' : 'var(--landing-text-soft)'} />
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 500, 
                      color: 'var(--landing-text)',
                      marginBottom: '0.25rem'
                    }}>
                      Face Detection
                    </div>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: 'var(--landing-text-soft)' 
                    }}>
                      Detects if images contain human faces
                    </div>
                  </div>
                </label>

                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  cursor: 'pointer',
                  padding: '0.75rem',
                  borderRadius: '0',
                  border: '1px solid var(--landing-border)',
                  background: globalAiImageDetect ? 'var(--landing-accent-soft)' : 'transparent',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={globalAiImageDetect}
                    onChange={(e) => setGlobalAiImageDetect(e.target.checked)}
                    style={{ 
                      width: '18px', 
                      height: '18px', 
                      cursor: 'pointer',
                      accentColor: 'var(--landing-accent)'
                    }}
                  />
                  <Brain size={18} color={globalAiImageDetect ? 'var(--landing-accent)' : 'var(--landing-text-soft)'} />
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 500, 
                      color: 'var(--landing-text)',
                      marginBottom: '0.25rem'
                    }}>
                      AI Image Detection
                    </div>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: 'var(--landing-text-soft)' 
                    }}>
                      Detects if images are AI-generated
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Per-Image Configuration */}
            <div className="threshold-section">
              <label className="threshold-label">Per-Image Configuration (Optional)</label>
              <p className="webhook-help" style={{ marginBottom: '1rem' }}>
                Override global settings for specific image positions (0-9). Images without per-image config will use global settings.
              </p>

              {/* Add new per-image config */}
              <div style={{ 
                display: 'flex', 
                gap: '0.5rem', 
                marginBottom: '1.5rem',
                padding: '1rem',
                background: 'var(--landing-bg-soft)',
                border: '1px solid var(--landing-border)'
              }}>
                <input
                  type="number"
                  min="0"
                  max="9"
                  value={newImageIndex}
                  onChange={(e) => setNewImageIndex(e.target.value)}
                  placeholder="Image index (0-9)"
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#ffffff',
                    border: '1px solid var(--landing-border)',
                    borderRadius: '0',
                    color: 'var(--landing-text)',
                    fontSize: '0.875rem'
                  }}
                />
                <button
                  onClick={addPerImageConfig}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: 'var(--landing-accent-soft)',
                    border: '1px solid var(--landing-accent-border)',
                    borderRadius: '0',
                    color: 'var(--landing-accent)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  <Plus size={16} />
                  Add
                </button>
              </div>

              {/* List of per-image configs */}
              {sortedImageIndices.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {sortedImageIndices.map((index) => {
                    const config = perImageSettings[index];
                    return (
                      <div
                        key={index}
                        style={{
                          padding: '1.5rem',
                          background: 'var(--landing-bg-soft)',
                          border: '1px solid var(--landing-border)',
                          borderLeft: '3px solid var(--landing-accent)'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '1rem'
                        }}>
                          <div style={{ fontWeight: 500, color: 'var(--landing-text)' }}>
                            Image Index {index}
                          </div>
                          <button
                            onClick={() => removePerImageConfig(index)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.5rem 1rem',
                              background: 'transparent',
                              border: '1px solid #ef4444',
                              borderRadius: '0',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '0.875rem'
                            }}
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.75rem',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={config.face_detect !== undefined ? config.face_detect : globalFaceDetect}
                              onChange={(e) => updatePerImageConfig(index, 'face_detect', e.target.checked)}
                              style={{ 
                                width: '18px', 
                                height: '18px', 
                                cursor: 'pointer',
                                accentColor: 'var(--landing-accent)'
                              }}
                            />
                            <Eye
                              size={16}
                              color={config.face_detect !== undefined
                                ? (config.face_detect ? 'var(--landing-accent)' : 'var(--landing-text-soft)')
                                : 'var(--landing-text-soft)'
                              }
                            />
                            <span style={{ color: 'var(--landing-text-muted)', fontSize: '0.875rem' }}>
                              Face Detection {config.face_detect === undefined && '(uses global)'}
                            </span>
                          </label>

                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '0.75rem',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              checked={config.ai_image_detect !== undefined ? config.ai_image_detect : globalAiImageDetect}
                              onChange={(e) => updatePerImageConfig(index, 'ai_image_detect', e.target.checked)}
                              style={{ 
                                width: '18px', 
                                height: '18px', 
                                cursor: 'pointer',
                                accentColor: 'var(--landing-accent)'
                              }}
                            />
                            <Brain
                              size={16}
                              color={config.ai_image_detect !== undefined
                                ? (config.ai_image_detect ? 'var(--landing-accent)' : 'var(--landing-text-soft)')
                                : 'var(--landing-text-soft)'
                              }
                            />
                            <span style={{ color: 'var(--landing-text-muted)', fontSize: '0.875rem' }}>
                              AI Image Detection {config.ai_image_detect === undefined && '(uses global)'}
                            </span>
                          </label>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {sortedImageIndices.length === 0 && (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: 'var(--landing-text-soft)',
                  background: 'var(--landing-bg-soft)',
                  border: '1px solid var(--landing-border)',
                  fontSize: '0.875rem'
                }}>
                  No per-image configurations. All images will use global settings.
                </div>
              )}
            </div>

            {/* Image Block Mode */}
            <div className="threshold-section">
              <label className="threshold-label">Image Block Mode</label>
              <div style={{ 
                marginTop: '1rem',
                padding: '1rem',
                background: 'var(--landing-bg-soft)',
                border: '1px solid var(--landing-border)',
                borderLeft: '3px solid #f59e0b'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={imageBlockEnabled}
                    onChange={(e) => setImageBlockEnabled(e.target.checked)}
                    style={{ 
                      width: '18px', 
                      height: '18px', 
                      cursor: 'pointer',
                      accentColor: 'var(--landing-accent)'
                    }}
                  />
                  <Shield size={18} color={imageBlockEnabled ? '#f59e0b' : 'var(--landing-text-soft)'} />
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: 500, 
                      color: 'var(--landing-text)',
                      marginBottom: '0.25rem'
                    }}>
                      Image Block Mode
                    </div>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: 'var(--landing-text-soft)' 
                    }}>
                      When enabled, overrides text analysis and outputs trust score 0 if any image checks fail
                    </div>
                  </div>
                </label>
              </div>
            </div>

          </div>
        </div>

        <div className="threshold-modal-footer">
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button className="save-button" onClick={handleSave}>
            <Save size={16} />
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageSettingsModal;
